import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppIcons } from "./AppIcons";
import { AppUpdateEvery } from "./AppUpdateEvery";

// AppAccount - the RULES of the three account doors the archive page grew on 2026-09-24: signing in
// on a new device (`#signin`), linking WhatsApp again from an open archive (the re-link banner), and
// closing the account (`#leave`). No DOM, no `XMLHttpRequest`, no timers.
//
// ⚠ RULES APART FROM TRANSPORT, FOR THE MEASURED REASON `AppSignUpApi` RECORDS: the Bridge suite
// imports this file to pin the `/leave` answer's field names and sentences against `LeaveRules`
// (`LeaveContract.test.ts`), and a transport in here would pass under tsx and fail under tsc. The
// request half is `AppAccountPost`.
//
// ==================================================================================================
// !! ⛔ EVERY SENTENCE THESE DOORS SHOW IS VALIDATION, SHOWN IN INK
// ==================================================================================================
//
// A refused sign-in code, a leave that must be pressed again, a WhatsApp link the service will not
// re-ask for - all of it is the product working (law 1, fate 1). None of it goes to
// `KGlobalErrorHandler`, and none of it is painted in the alarm orange of `.trouble`: on the leave
// page in particular an orange sentence reads as "something broke while deleting my account", which
// is the one impression that page must never give.

/** One line of what `/leave` did. Must match `LeaveRules.AnswerFor`'s `steps` rows. */
export class AppLeaveStep {
    public Name: string = "";
    public Done: boolean = false;
    /** ⚠ False only for the WhatsApp unlink, which the subscriber can finish on the phone. */
    public Blocking: boolean = true;
    public Say: string = "";
}

/** The whole `/leave` answer. */
export class AppLeaveAnswer {
    public Ok: boolean = false;
    /** ⛔ The account is closed. Read as strictly `true`. */
    public Left: boolean = false;
    public Why: string = "";
    public Steps: AppLeaveStep[] = [];
    /** What stays the subscriber's - the folder in their own Dropbox. */
    public Kept: string = "";
}

/** The leave page, derived from what is known - never a remembered position. */
export class AppLeaveState {
    /** The number this browser is signed in as, or "" when it holds no key. */
    public Target: string = "";
    /** What is typed in the confirm box. */
    public Typed: string = "";
    /** A request is in flight. */
    public Busy: boolean = false;
    /** The last answer, or null before the first press. */
    public Answer: AppLeaveAnswer | null = null;
}

/** The sign-in page. */
export class AppSignInState {
    /** The Dropbox page to open, as the service built it. "" until it arrives (or if refused). */
    public Url: string = "";
    /** True once `GET /signin` has answered, either way. */
    public Asked: boolean = false;
    /** Why there is no Dropbox link, or "". */
    public UrlWhy: string = "";
    /** The number in the box. Kept across a refusal. */
    public Number: string = "";
    /** The refusal of the last attempt, or "". */
    public Why: string = "";
    public Busy: boolean = false;
}

/** The re-link banner over an open archive. */
export class AppRelinkState {
    /** The service said WhatsApp is NOT linked (never inferred from silence). */
    public Show: boolean = false;
    /** The ask is in flight. */
    public Busy: boolean = false;
    /** An ask was accepted and no code has arrived yet. */
    public Waiting: boolean = false;
    /** The pairing code to type, or "". ⛔ Never persisted - see `SignUpState.Code`. */
    public Code: string = "";
    public Why: string = "";
    /** The phone is linked again. */
    public Linked: boolean = false;
}

/** How one leave step is drawn. */
export class AppLeaveMark {
    /** `done`, `failed` or `todo`. */
    public Kind: string = "";
    /** The glyph: a tick, a cross or an open circle. */
    public Glyph: string = "";
}

export class AppAccount {
    /** The routes. ⚠ Checked BEFORE the folder guard, like `#signup` - none of them needs a folder. */
    public static readonly SignInRoute: string = "signin";
    public static readonly LeaveRoute: string = "leave";
    /**
     * "Import a backup" - bring a history AFTER sign-up (2026-09-24). ⚠ Before the folder guard too:
     * a subscriber who skipped the import may have a folder that is not built yet, and the one thing
     * that fixes that must not depend on the folder opening first.
     */
    public static readonly ImportRoute: string = "import";
    /** ⚠ AFTER the folder guard - it is a tab of an open archive. */
    public static readonly SentRoute: string = "sent";

    /** ⛔ Must match `LeaveRules.Route`. */
    public static readonly LeavePath: string = "/leave";

    public static readonly StepUnlink: string = "whatsapp";

    /**
     * Read `/leave`'s answer.
     *
     * ⛔ THE BODY IS READ WHATEVER THE STATUS - a 429 carries "too many attempts" in the body, and a
     * transport that threw on it would replace the only sentence that says what to do.
     * ⚠ STATUS 0 OR AN UNREADABLE BODY IS `OursWhy`: our end, never the subscriber's.
     */
    public static ParseLeave = (Status: number, Text: string, OursWhy: string): AppLeaveAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppAccount.NoLeave(OursWhy); }
        try {
            if (Status === 0) { return AppAccount.NoLeave(OursWhy); }
            const Raw: unknown = JSON.parse(nz(Text));
            if (Raw === null || typeof Raw !== "object") { return AppAccount.NoLeave(OursWhy); }
            const Out: AppLeaveAnswer = AppAccount.LeaveFields(Raw as Record<string, unknown>);
            // ⚠ A REFUSAL WITH NO SENTENCE STILL GETS ONE - a blank "no" explains nothing.
            if (Out.Left !== true && Out.Why.trim().length === 0 && Out.Steps.length === 0) {
                Out.Why = OursWhy;
            }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an answer this build cannot parse is EXPECTED on a public
            // wire (a proxy error page, a truncated reply) - fate 1, shown as our end.
            return AppAccount.NoLeave(OursWhy);
        }
    };

    /** DOCUMENTED EXCEPTION: reads fields already parsed; called only from the guarded `ParseLeave`.
     *  ERROR-SUPPRESSED-JUSTIFIED: pure reader. ⛔ Booleans are read as strictly `true`. */
    private static LeaveFields = (Raw: Record<string, unknown>): AppLeaveAnswer => {
        const Out: AppLeaveAnswer = new AppLeaveAnswer();
        Out.Ok = Raw.ok === true;
        Out.Left = Raw.left === true;
        Out.Why = AppAccount.Str(Raw.why);
        Out.Kept = AppAccount.Str(Raw.kept);
        if (Array.isArray(Raw.steps) === false) { return Out; }
        for (const One of Raw.steps as unknown[]) {
            if (One === null || typeof One !== "object") { continue; }
            const Row: Record<string, unknown> = One as Record<string, unknown>;
            const Step: AppLeaveStep = new AppLeaveStep();
            Step.Name = AppAccount.Str(Row.step);
            Step.Done = Row.done === true;
            // ⚠ ONLY AN EXPLICIT `false` MAKES A STEP OPTIONAL. Reading absence as optional would let
            // a failed delete be drawn as a harmless to-do.
            Step.Blocking = Row.blocking !== false;
            Step.Say = AppAccount.Str(Row.say);
            Out.Steps.push(Step);
        }
        return Out;
    };

    /** DOCUMENTED EXCEPTION: a string, or "". ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    private static Str = (Value: unknown): string => {
        if (typeof Value !== "string") { return ""; }
        return Value;
    };

    /** DOCUMENTED EXCEPTION: builds a refusal from a sentence in hand. ERROR-SUPPRESSED-JUSTIFIED:
     *  cannot throw. */
    public static NoLeave = (Why: string): AppLeaveAnswer => {
        const Out: AppLeaveAnswer = new AppLeaveAnswer();
        Out.Why = nz(Why);
        return Out;
    };

    /**
     * How one step is drawn.
     *
     * ⛔ A TICK ONLY ON THE SERVICE'S OWN `done`. A blocking step not done is a CROSS - "deleted"
     * beside something still there is the one lie this page must never tell. ⚠ The unlink
     * (`blocking: false`) not done is a TO-DO, not a failure: the account is closed either way and
     * the step's own sentence says how to finish it on the phone.
     */
    public static MarkOf = (Step: AppLeaveStep): AppLeaveMark => {
        if (getCoreUtils().KErrorMode === true) { return new AppLeaveMark(); }
        try {
            const Out: AppLeaveMark = new AppLeaveMark();
            if (Step.Done === true) { Out.Kind = "done"; Out.Glyph = AppIcons.Svg(AppIcons.Check, 14); return Out; }
            if (Step.Blocking === false) { Out.Kind = "todo"; Out.Glyph = ""; return Out; }
            Out.Kind = "failed";
            Out.Glyph = AppIcons.Svg(AppIcons.Close, 14);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.MarkOf");
            return new AppLeaveMark();
        }
    };

    /**
     * Has the subscriber typed their own number?
     *
     * ⚠ DIGITS ONLY, SO SPACES AND DASHES DO NOT MATTER - the service compares as E.164 too
     * (`LeaveRules.ConfirmRefusal`). ⛔ AND IT MUST START WITH `+`: the box says so, and a button
     * that lit up for "46 73..." would send a request the service then refuses.
     */
    public static ConfirmMatches = (Typed: string, Target: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const One: string = nz(Typed).trim();
            if (One.charAt(0) !== "+") { return false; }
            const A: string = AppAccount.Digits(One);
            const B: string = AppAccount.Digits(Target);
            return A.length > 0 && A === B;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.ConfirmMatches");
            return false;
        }
    };

    /** DOCUMENTED EXCEPTION: the digits of a string in hand. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static Digits = (Text: string): string => {
        return nz(Text).replace(/[^0-9]/g, "");
    };

    /** DOCUMENTED EXCEPTION: pure digit comparison, independent of the error gate.
     * ERROR-SUPPRESSED-JUSTIFIED: callers retain their own error boundary and minimum length. */
    public static MatchingNumbers = (Typed: string, Held: string, MinDigits: number = 1): boolean => {
        const Left: string = AppAccount.Digits(Typed);
        const Right: string = AppAccount.Digits(Held);
        const Minimum: number = Math.max(1, MinDigits);
        return Left.length >= Minimum && Right.length >= Minimum && Left === Right;
    };

    /**
     * May the Close button be pressed right now?
     *
     * ⛔ NEVER WHILE A REQUEST IS OUT. Two leaves racing both stop, both revoke and both delete, and
     * their answers would contradict each other on the page.
     */
    public static MayLeave = (State: AppLeaveState): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (State.Busy === true) { return false; }
            if (State.Answer !== null && (State.Answer as AppLeaveAnswer).Left === true) { return false; }
            if (nz(State.Target).length === 0) { return false; }
            return AppAccount.ConfirmMatches(State.Typed, State.Target);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.MayLeave");
            return false;
        }
    };

    /**
     * The button's word. ⚠ "Try again" once an attempt has been answered without closing the
     * account - the service carries on from where it stopped, and the page should say so.
     */
    public static LeaveWord = (State: AppLeaveState): string => {
        if (getCoreUtils().KErrorMode === true) { return "Close my account"; }
        try {
            if (State.Answer !== null && (State.Answer as AppLeaveAnswer).Steps.length > 0) {
                return "Try again";
            }
            return "Close my account";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.LeaveWord");
            return "Close my account";
        }
    };

    /**
     * Does this refusal mean the browser's key is no longer any good?
     *
     * ⚠ THEN THE PAGE FORGETS IT - otherwise the footer keeps offering "Close my account" to a
     * browser that can no longer prove anything, and every press is a doomed round trip.
     * ⛔ ONLY ON THAT SENTENCE: forgetting on "too many attempts" or "at our end" would log a
     * working browser out over a blip.
     */
    public static IsStale = (Answer: AppLeaveAnswer): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Answer.Left === true || Answer.Steps.length > 0) { return false; }
            return nz(Answer.Why).trim() === AppAccount.LeaveStaleWhy;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.IsStale");
            return false;
        }
    };

    /** ⛔ Must equal `LeaveRules.StaleWhy()` - `LeaveContract.test.ts` pins it. */
    public static readonly LeaveStaleWhy: string = "this page is not signed in to a WACommander"
        + " account any more. If you have just left, that is finished. Otherwise sign in on this"
        + " device, or sign up if you have not yet.";

    /**
     * What stays theirs, shown BEFORE the first press. ⛔ Must equal `LeaveRules.KeptSay()` (pinned),
     * so the promise made before the press is the one the service repeats after it.
     */
    public static readonly KeptSay: string = "Your archive stays in your own Dropbox, in the folder"
        + " Apps/WACommander, and we have not touched it - it is yours to keep or delete. Messages"
        + " that arrived since it was last updated (" + AppUpdateEvery.AtMost + ") are not in it; they are"
        + " still on your phone.";

    /**
     * A service sentence, ready to stand on its own.
     *
     * ⚠ THE SERVICE WRITES ITS REFUSALS TO FOLLOW A PREFIX ("this page is not signed in..."), so
     * shown alone they start lower-case and read as a fragment - `AppSignUp.Note`'s rule.
     */
    public static Sentence = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const One: string = nz(Why).trim();
            if (One.length === 0) { return ""; }
            return One.charAt(0).toUpperCase() + One.substring(1);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.Sentence");
            return "";
        }
    };

    /**
     * The body of a re-link ask or poll: the archive key INSTEAD of a sign-up secret.
     *
     * ⛔ EXACTLY ONE PROOF - `PairHttpRules.AskIn` refuses a body carrying both, so no `secret` field
     * is sent at all. ⛔ The action is always explicit, and only `ask` or `poll`: each new code
     * cancels the one before it, so a defaulted action is the cancellation `/pair` exists to prevent.
     */
    public static RelinkBody = (Phone: string, Key: string, Action: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            if (Action !== "ask" && Action !== "poll") { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(), action: Action });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.RelinkBody");
            return "";
        }
    };

    /**
     * Which session secret to send to `/folder`.
     *
     * ⛔ NONE ONCE THIS PAGE ALREADY HOLDS A KEY FROM THIS SESSION. The service prefers the secret
     * because it MINTS - and every mint adds a key to a list capped at five, least-recently-used
     * evicted. The sign-up now asks for a key as soon as it can (so a subscriber is never left
     * keyless after linking), and sending the secret again on "Open WACommander" would mint a second
     * one and evict another of their devices for nothing.
     */
    public static FolderSecret = (Secret: string, SessionKey: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(SessionKey).trim().length > 0) { return ""; }
            return nz(Secret).trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.FolderSecret");
            return "";
        }
    };

    /**
     * Does a refused `/folder` answer mean the stored archive key is dead?
     *
     * ⛔ ONLY ON THE SERVICE'S `stale` FLAG (2026-10-22, live report: a subscriber lost her valid 30-day key
     * to a deploy). Status 0, a 5xx, `NotNowWhy` and an older service without the flag all KEEP the key.
     * ⚠ And only on the key path: a refusal of the SECRET says nothing about a stored key.
     */
    public static ForgetsFolderKey = (Stale: boolean, SecretUsed: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (nz(SecretUsed).trim().length > 0) { return false; }
            return Stale === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.ForgetsFolderKey");
            return false;
        }
    };

    /** How many quiet re-asks of `/folder` after a refusal that is not a verdict on the key. */
    public static readonly FolderRetries: number = 6;

    /**
     * Milliseconds before re-asking `/folder` after a transient refusal (attempt 0-based), or -1 = stop and
     * show the sentence. 2, 4, 8, 15, 15, 15 s - covers a deploy's restart window.
     */
    public static FolderRetryMs = (Attempt: number): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            if (!(Attempt >= 0) || Attempt >= AppAccount.FolderRetries) { return -1; }
            return Math.min(15000, 2000 * Math.pow(2, Math.floor(Attempt)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.FolderRetryMs");
            return -1;
        }
    };

    /**
     * Should a refused `/folder` answer become the Dropbox step, rather than a dead end?
     *
     * ⭐ THE STUCK SIGN-UP (2026-09-24): a reload after WhatsApp linked and before Dropbox was
     * connected lost the sign-up secret, the number was live so `/claim` refused it, and sign-in
     * needs a Dropbox that was never connected. The page still holds the key `/folder` handed back
     * with its "no storage" refusal - and `/dropbox` now takes that key while no grant is stored.
     * ⛔ ONLY ON THE SERVICE'S OWN `nostorage` FLAG, AND ONLY WITH A KEY AND A NUMBER IN HAND: without
     * the key the Dropbox step would refuse on the first press, which is a dead end with a button.
     */
    public static ResumesDropbox = (NoStorage: boolean, Key: string, Phone: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (NoStorage !== true) { return false; }
            return nz(Key).trim().length > 0 && nz(Phone).trim().length > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.ResumesDropbox");
            return false;
        }
    };

    /**
     * Is the number typed into sign-up the one this browser already holds a key for?
     *
     * ⚠ DIGITS ONLY, because the box takes "+46 73 ..." with spaces and the store holds the service's
     * E.164. ⭐ It is how a reload that landed back on `#signup` finds its way on: the claim is refused
     * (the number is live), and a key for THAT number means this is the same subscriber coming back,
     * not a stranger - so the archive (or its unfinished Dropbox step) opens instead of a dead end.
     * ⛔ "" never matches "" - an empty box must not open whatever key the browser holds.
     */
    public static SameNumber = (Typed: string, Held: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppAccount.MatchingNumbers(Typed, Held);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.SameNumber");
            return false;
        }
    };

    /** The first non-empty value. DOCUMENTED EXCEPTION: picks between values in hand.
     *  ERROR-SUPPRESSED-JUSTIFIED: pure, cannot throw. */
    public static First = (One: string, Two: string): string => {
        if (nz(One).trim().length > 0) { return nz(One).trim(); }
        return nz(Two).trim();
    };

    /**
     * What to do with a re-link poll's answer: `linked`, `code`, `refused` or `wait`.
     *
     * ⛔ `linked` FIRST, THEN A CODE, THEN A REFUSAL - `AppMain.OnPairState`'s order, for its reason:
     * linked is the one answer that does not expire. ⚠ A lost connection (`ok:false` with the
     * transport's own sentence) is `wait`, never a verdict - stopping would abandon an ask that is
     * being answered, and the obvious recovery (ask again) cancels the code on its way.
     */
    public static RelinkVerdict = (Ok: boolean, Linked: boolean, Code: string, Waiting: boolean,
                                   Why: string, OursWhy: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "wait"; }
        try {
            if (Linked === true) { return "linked"; }
            if (nz(Code).length > 0) { return "code"; }
            if (Ok === true || Waiting === true) { return "wait"; }
            if (nz(Why) === nz(OursWhy)) { return "wait"; }
            return "refused";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccount.RelinkVerdict");
            return "wait";
        }
    };
}
